import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Input,
  Modal,
  PrioSpinner,
  Select,
  Tooltip,
  useTheme,
} from '@prio365/prio365-react-library';
import { notification } from 'antd';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCurrentFolderItem,
  getDocumentsMetaState,
  getDocumentsWidgetActiveProjectId,
  getProject,
  RootReducerState,
} from '../../../../apps/main/rootReducer';
import Flex from '../../../../components/Flex';
import useDebounce from '../../../../hooks/useDebounce';
import { PrioFile } from '../../../../components/Upload/UploadField';
import { Configuration } from '../../../../models/Configuration';
import { CopyDriveItem, DriveItem } from '../../../../models/Drive';
import {
  MessageAttachment,
  SaveAttachmentsToCloud,
} from '../../../../models/Message';
import { Project } from '../../../../models/Project';
import {
  ConfigurationKeys,
  DriveItemId,
  ProjectId,
} from '../../../../models/Types';
import { PrioTheme } from '../../../../theme/types';
import { makePrioStyles } from '../../../../theme/utils';
import { formatHumanFileSize, urltoFile } from '../../../../util';
import {
  apiSaveAttachmentsToCloudWithChunking,
  apiUploadAttachments,
} from '../../../mail/api';
import { apiFetchConfigurations } from '../../../settings/api';
import {
  copyItemsIntoFolder,
  DriveItemsFetchContext,
  fetchDriveItemsSagaAction,
} from '../../actions';
import { setDocumentsMetaState } from '../../actions/meta';
import { addPrioFilesToUploadList } from '../../actions/uploadLists';
import { apiFetchDocumentPrefix } from '../../api';
import {
  sagaStartUploadFiles,
  sagaUploadFiles,
} from '../../sagas/watchUploadFiles';
import {
  checkFileNameExists,
  checkFileNamesExists,
  checkIfOneNote,
  colorForMimeType,
  iconForMimeType,
  isDriveItemFolder,
} from '../../util';
import { resolveTypedFullName } from '../../util/resolveTypedFullName';
import FolderSelectionDrawer from '../Drawers/FolderSelectionDrawer';
import DriveItemBreadcrumb from '../DriveItemBreadcrumb/DriveItemBreadcrumb';

export enum FileNameConflictBehaviour {
  REPLACE = 'replace', // <--- Vorhandene Datei überschreiben
  RENAME = 'rename', // <--- Kopien anlegen (z.B. "Datei_1")
  FAIL = 'fail',
}

const useStyles = makePrioStyles((theme) => ({
  root: { minWidth: 'fit-content' },
  conflictItem: {
    borderTop: `1px solid ${theme.colors.application.border}`,
    padding: theme.spacing.small,

    '&:last-child': {
      borderBottom: `1px solid ${theme.colors.application.border}`,
    },
  },
  conflictItemsWrapper: { maxHeight: '300px', overflowY: 'scroll' },
  conflictBehaviourSelectWrapper: { maxWidth: 'fit-content' },
  selectForEachItem: {
    borderRadius: theme.borderRadius.small,
    background: theme.colors.application.background.light,
    padding: theme.spacing.small,
  },
  inlineInput: { width: '400px' },
}));

interface DriveItemConflictModalProps {
  className?: string;
  visible: boolean;
  onClose: () => void;
}

export interface DriveItemConflictModalState {
  behavior: 'copy' | 'move' | 'upload';
  context: DriveItemsFetchContext;
  modelType: 'driveItem' | 'messageAttachment' | 'file';
  selectedDriveItems?: DriveItem[];
  selectedAttachments?: MessageAttachment[];
  mailId?: string;
  sourceFolderId?: DriveItemId;
  sourceProjectId?: ProjectId;
  destinationProjectId: ProjectId;
  destinationFolderId: DriveItemId;
  sessionId?: string;
  fileList?: PrioFile[];
  existingCheckedNames?: any;
}

const DriveItemConflictModal: React.FC<DriveItemConflictModalProps> = (
  props
) => {
  //#region ------------------------------ Defaults
  const { className, visible, onClose } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  const dispatch = useDispatch();
  const firstInputRef = useRef<any>(null);

  const { driveItemConflictModalState } =
    useSelector(getDocumentsMetaState) || {};

  const {
    behavior,
    context,
    modelType,
    selectedDriveItems,
    selectedAttachments,
    mailId: initialMailId,
    destinationFolderId: initialDestinationFolderId,
    destinationProjectId: initialDestinationProjectId,
    sourceFolderId: initialSourceFolderId,
    sourceProjectId: initialSourceProjectId,
    sessionId,
    fileList,
    existingCheckedNames,
  } = driveItemConflictModalState || {};

  const conflictActions: Array<{
    label: string;
    value: string;
    onClick?: () => void;
  }> = [
    {
      label: t('documents:duplicatedDriveItemsModal.actions.copyMultiple'),
      value: FileNameConflictBehaviour.RENAME,
      onClick: () => applyToAll(FileNameConflictBehaviour.RENAME),
    },
    {
      label: t('documents:duplicatedDriveItemsModal.actions.overwriteMultiple'),
      value: FileNameConflictBehaviour.REPLACE,
      onClick: () => applyToAll(FileNameConflictBehaviour.REPLACE),
    },
  ];
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [
    selectTargetFolderSubDrawerVisible,
    setSelectTargetFolderSubDrawerVisible,
  ] = useState<boolean>(false);

  const [destinationProjectId, setDestinationProjectId] = useState<ProjectId>(
    initialDestinationProjectId ?? initialSourceProjectId
  );

  const [destinationFolderId, setDestinationFolderId] = useState<DriveItemId>(
    initialDestinationFolderId ?? initialSourceFolderId
  );

  const sourceProject = useSelector<RootReducerState, Project>((state) =>
    getProject(state, initialSourceProjectId)
  );

  const sourceFolderIsRoot =
    !initialSourceFolderId || !!initialSourceFolderId.match(/^root-group-/);

  const destinationProject = useSelector<RootReducerState, Project>((state) =>
    getProject(state, destinationProjectId)
  );

  const destinationFolder = useSelector<RootReducerState, DriveItem>((state) =>
    getCurrentFolderItem(
      state,
      destinationFolderId ?? `root-group-${destinationProject?.groupId}`
    )
  );

  const isRootGroupId = destinationFolderId?.startsWith('root-group-');

  const isDestinationFolderLoaded =
    !!destinationFolder &&
    ((isRootGroupId && !!destinationFolder.id) ||
      (!isRootGroupId && destinationFolder.id === destinationFolderId));

  const [updatedFileNames, setUpdatedFileNames] = useState<
    Record<string, string>
  >({});

  const [sourceMailId, setSourceMailId] = useState<string>(
    initialMailId ?? null
  );

  const [existingItems, setExistingItems] = useState<string[]>([]);

  const [copyProcessing, setCopyProcessing] = useState<boolean>(false);
  const [fileCheckProcessing, setFileCheckProcessing] =
    useState<boolean>(false);

  const [loadingByItemId, setLoadingByItemId] = useState<
    Record<string, boolean>
  >({});

  const [inputValues, setInputValues] = useState<Record<string, string>>({});

  const [pendingNameCheck, setPendingNameCheck] = useState<{
    itemId: string;
    name: string;
    extension: string;
  } | null>(null);

  // True from the moment a new name is typed until its conflict check has
  // resolved. Combined with loadingByItemId it blocks OK/Enter so the user
  // cannot confirm while a check is still pending.
  const [isCheckingName, setIsCheckingName] = useState<boolean>(false);

  const debouncedPendingNameCheck = useDebounce(pendingNameCheck, 500);

  const handleFilenameChangeRef =
    useRef<
      (itemId: string, newBaseName: string, extension: string) => Promise<void>
    >(null);

  const isNameCheckPending =
    isCheckingName || Object.values(loadingByItemId).some(Boolean);

  const [conflictBehaviourByItem, setConflictBehaviourByItem] = useState<
    Record<string, FileNameConflictBehaviour>
  >({});

  const [selectedForAllBehavior, setSelectedForAllBehavior] =
    useState<FileNameConflictBehaviour>(null);
  const requestIdRef = useRef(0);

  const documentsWidgetActiveProjectId = useSelector(
    getDocumentsWidgetActiveProjectId
  );

  const [configurations, setConfigurations] = useState<Configuration[] | null>(
    null
  );

  const useDocumentsPrefix =
    configurations?.find(
      (config) =>
        config.key === ConfigurationKeys.USE_PREFIX_TO_SAVE_ATTACHMENT_TO_CLOUD
    )?.value === 'true';

  const [prefix, setPrefix] = useState<string>(null);

  const [pseudoFolders, setPseudoFolders] = useState<
    {
      id: string;
      name: string;
      type: 'PseudoFolder';
      size: number;
    }[]
  >([]);

  const [uploadedFiles, setUploadedFiles] = useState<PrioFile[]>([]);

  const [showContent, setShowContent] = useState(false);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const getPseudoFolders = useCallback(() => {
    if (!fileList) return;
    const folderMap = new Map<string, number>();

    fileList.forEach((file) => {
      if (file.webkitRelativePath && file.webkitRelativePath.includes('/')) {
        const folderName = file.webkitRelativePath.split('/')[0];
        folderMap.set(
          folderName,
          (folderMap.get(folderName) || 0) + (file.size || 0)
        );
      }
    });

    setPseudoFolders(
      Array.from(folderMap.entries()).map(([name, size]) => ({
        id: `pseudo-folder-${name.replace(/\s+/g, '-').toLowerCase()}`,
        name,
        type: 'PseudoFolder' as const,
        size,
      }))
    );
  }, [fileList]);

  const getUploadedFiles = useCallback(() => {
    if (!fileList) return;
    // Nur Dateien ohne Ordner (kein Slash im webkitRelativePath)
    setUploadedFiles(
      fileList.filter((file) => !file.webkitRelativePath?.includes('/'))
    );
  }, [fileList]);

  const fetchExistingItems = useCallback(async () => {
    if (!selectedDriveItems && !selectedAttachments && !fileList) return;

    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;
    setExistingItems([]);

    let checkedNames = existingCheckedNames;
    if (!checkedNames) {
      const allNamesToCheck = [
        ...(selectedDriveItems ?? []).map((driveItem) => driveItem.name),
        ...(selectedAttachments ?? []).map((attachment) => attachment.name),
        ...(uploadedFiles ?? []).map((file) => file.name),
        ...(pseudoFolders ?? []).map((folder) => folder.name),
      ];
      checkedNames = await checkFileNamesExists(
        destinationProject?.groupId,
        destinationFolder?.id ?? null,
        allNamesToCheck
      );
    }

    if (currentRequestId !== requestIdRef.current) return;

    const folderResponse = checkedNames?.find(
      (folder) => folder.parentFolderId === destinationFolder.id
    );

    if (folderResponse) {
      const items = [
        ...(selectedDriveItems || []),
        ...(selectedAttachments || []),
        ...(uploadedFiles || []).map((item) => ({
          ...item,
          id: item.fileId,
          name: item.name,
        })),
        ...(pseudoFolders || []),
      ].filter((item) =>
        folderResponse.fileNames.some(
          (checked) => checked.fileName === item.name && checked.exists
        )
      );
      setExistingItems(items.map((item) => item.id));
    } else {
      setExistingItems([]);
    }

    setFileCheckProcessing(false);
  }, [
    selectedDriveItems,
    selectedAttachments,
    fileList,
    destinationProject?.groupId,
    destinationFolder?.id,
    pseudoFolders,
    uploadedFiles,
    existingCheckedNames,
  ]);

  const debouncedFetchExistingItems = useMemo(() => {
    return debounce(fetchExistingItems, 300);
  }, [fetchExistingItems]);

  const handleBehaviorChange = (
    itemId: string,
    behavior: FileNameConflictBehaviour
  ) => {
    setConflictBehaviourByItem((prev) => ({ ...prev, [itemId]: behavior }));
  };

  const getUpdatedPath = (webkitRelativePath: string, name: string) => {
    const pseudoFolder = pseudoFolders.find((folder) =>
      webkitRelativePath.includes(folder.name)
    );

    let path = webkitRelativePath;
    if (webkitRelativePath && pseudoFolder) {
      const parts = webkitRelativePath.split('/');
      if (parts.length > 0) {
        parts[0] = updatedFileNames[pseudoFolder.id] ?? pseudoFolder.name;
        parts[parts.length - 1] = name;
        path = parts.join('/');
      }

      return path;
    }
    return name;
  };

  const handleOkClicked = async () => {
    const isMove = behavior === 'move';
    if (modelType === 'driveItem') {
      await handleCopy(isMove);
    } else if (modelType === 'file') {
      if (fileList) {
        dispatch(
          sagaStartUploadFiles(
            destinationProject.groupId,
            sessionId,
            destinationFolderId,
            destinationProjectId
          )
        );
        dispatch(
          addPrioFilesToUploadList(
            destinationProject.groupId,
            fileList.map(
              ({
                fileId,
                name,
                webkitRelativePath,
                size,
                type,
                sessionId,
              }) => ({
                fileId,
                sessionId,
                parentDriveItemId:
                  destinationFolder?.id ??
                  `root-group-${destinationProject.groupId}`,
                name: updatedFileNames[fileId] ?? name,
                path: getUpdatedPath(
                  webkitRelativePath,
                  updatedFileNames[fileId] ?? name
                ),
                size,
                isFolder: false,
                mimeType: type,
                driveItemId: null,
              })
            )
          )
        );

        const filesByBehaviour: Record<FileNameConflictBehaviour, PrioFile[]> =
          {
            [FileNameConflictBehaviour.REPLACE]: [],
            [FileNameConflictBehaviour.RENAME]: [],
            [FileNameConflictBehaviour.FAIL]: [],
          };

        for (const item of fileList) {
          const behavior =
            conflictBehaviourByItem?.[item.fileId] ??
            FileNameConflictBehaviour.RENAME;
          filesByBehaviour[behavior].push(item);
        }

        for (const [behavior, items] of Object.entries(filesByBehaviour)) {
          if (items.length === 0) continue;

          dispatch(
            sagaUploadFiles(
              destinationProject.groupId,
              sessionId,
              destinationFolderId,
              destinationProjectId,
              items.map((file) => {
                const fileName = updatedFileNames[file.fileId] ?? file.name;
                const updatedFile = new File([file], fileName, {
                  type: file.type,

                  lastModified: file.lastModified,
                });
                const updatedPath = getUpdatedPath(
                  file.webkitRelativePath,
                  fileName
                );

                const prioFile = {
                  file: updatedFile,
                  fileId: file.fileId,
                  sessionId: file.sessionId,
                  name: fileName,
                  size: file.size,
                  type: file.type,
                  lastModified: file.lastModified,
                  webkitRelativePath: updatedPath,
                  arrayBuffer: updatedFile.arrayBuffer.bind(updatedFile),
                  slice: updatedFile.slice.bind(updatedFile),
                  stream: updatedFile.stream.bind(updatedFile),
                  text: updatedFile.text.bind(updatedFile),
                } as PrioFile;

                return prioFile;
              }),
              behavior as FileNameConflictBehaviour
            )
          );
        }
      }
    } else if (modelType === 'messageAttachment') {
      await saveAttachmentsToCloud();
    }

    onClose();
    resetModal();
  };

  const handleCopy = useCallback(
    async (deleteOriginals?: boolean) => {
      setCopyProcessing(true);

      const driveItemsToCopy: CopyDriveItem[] =
        selectedDriveItems.map<CopyDriveItem>((driveItem) => ({
          driveItemId: driveItem?.id,
          deleteSourceDriveItem: deleteOriginals ?? false,
          name:
            resolveTypedFullName(
              driveItem.name,
              inputValues[driveItem.id],
              isDriveItemFolder(driveItem)
            ) ??
            updatedFileNames[driveItem.id] ??
            driveItem.name,
        }));

      const itemsByBehavior: Record<
        FileNameConflictBehaviour,
        CopyDriveItem[]
      > = {
        [FileNameConflictBehaviour.REPLACE]: [],
        [FileNameConflictBehaviour.RENAME]: [],
        [FileNameConflictBehaviour.FAIL]: [],
      };

      for (const item of driveItemsToCopy) {
        const behavior =
          conflictBehaviourByItem?.[item.driveItemId] ??
          FileNameConflictBehaviour.RENAME;
        itemsByBehavior[behavior].push(item);
      }

      for (const [behavior, items] of Object.entries(itemsByBehavior)) {
        if (items.length === 0) continue;
        dispatch(
          copyItemsIntoFolder(
            initialSourceProjectId,
            sourceProject?.groupId,
            destinationProject?.groupId,
            items,
            selectedDriveItems.filter((i) =>
              items.some((it) => it.driveItemId === i.id)
            ),
            initialSourceFolderId,
            destinationFolder?.id,
            sourceFolderIsRoot,
            destinationFolder?.name === 'root',
            behavior as FileNameConflictBehaviour
          )
        );
      }

      setCopyProcessing(false);
    },
    [
      dispatch,
      initialSourceProjectId,
      sourceProject,
      destinationProject,
      initialSourceFolderId,
      destinationFolder,
      sourceFolderIsRoot,
      selectedDriveItems,
      conflictBehaviourByItem,
      updatedFileNames,
      inputValues,
    ]
  );

  const saveAttachmentsToCloud = async () => {
    setCopyProcessing(true);

    if (!!destinationFolderId) {
      const attachmentsByBehavior: Record<
        FileNameConflictBehaviour,
        SaveAttachmentsToCloud['attachments']
      > = {
        [FileNameConflictBehaviour.REPLACE]: [],
        [FileNameConflictBehaviour.RENAME]: [],
        [FileNameConflictBehaviour.FAIL]: [],
      };

      selectedAttachments.forEach((attachment) => {
        const behavior =
          conflictBehaviourByItem[attachment.id] ??
          FileNameConflictBehaviour.RENAME;
        attachmentsByBehavior[behavior].push({
          messageId: sourceMailId,
          attachmentId: attachment.id,
          fileName: `${useDocumentsPrefix ? prefix : ''}${updatedFileNames[attachment.id] ?? attachment.name}`,
          hasContentBytes: !!attachment.contentBytes,
        });
      });

      for (const [behavior, attachments] of Object.entries(
        attachmentsByBehavior
      )) {
        // dont saveAttachmentsToCloud if no contentBytes (indicator for smime)
        const attachmentsWithoutContentBytes = attachments.filter(
          (attachment) => !attachment.hasContentBytes
        );
        if (attachmentsWithoutContentBytes.length > 0) {
          const data: SaveAttachmentsToCloud = {
            targetDriveItemId: destinationFolder?.id ?? destinationFolderId,
            groupId: destinationProject?.groupId,
            attachments: attachmentsWithoutContentBytes,
            conflictBehaviour: behavior as FileNameConflictBehaviour,
          };

          const { result } = await apiSaveAttachmentsToCloudWithChunking(
            initialSourceProjectId,
            data
          );
          if (!(result.status >= 200 && result.status < 300)) {
            notification.open({
              message: t('common:error'),
              description: t(
                'mail:messageDisplay.errorMessages.saveAttachmentsToCloud'
              ),
            });
          } else {
            onRefresh();
          }
        }
      }

      const promises = selectedAttachments
        .filter(
          (attachment) =>
            attachment.contentBytes !== '' &&
            attachment.contentBytes !== null &&
            attachment.contentBytes !== undefined
        )
        .map((attachment) =>
          urltoFile(
            `data:${attachment.contentType};base64,${attachment.contentBytes}`,
            updatedFileNames[attachment.id] ?? attachment.name,
            attachment.contentType
          ).then(async (file) => {
            const { result: uploadResult } = await apiUploadAttachments(
              file,
              destinationProjectId !== 'me'
                ? destinationProjectId
                : documentsWidgetActiveProjectId,
              destinationProject?.groupId,
              destinationFolderId
            );
            if (!(uploadResult.status >= 200 && uploadResult.status < 300)) {
              return false;
            }
            return true;
          })
        );

      Promise.all(promises).then((values) => {
        if (values.includes(false)) {
          notification.open({
            message: t('common:error'),
            description: t(
              'mail:messageDisplay.errorMessages.saveAttachmentsToCloud'
            ),
          });
        }
        if (values.includes(true)) {
          onRefresh();
        }
      });
    }

    setCopyProcessing(false);
  };

  const onRefresh = useCallback(() => {
    if (!!destinationProject?.groupId) {
      dispatch(
        fetchDriveItemsSagaAction(
          destinationProjectId,
          destinationProject?.groupId,
          destinationFolderId,
          destinationFolder?.name === 'root',
          context
        )
      );
    }
  }, [
    dispatch,
    destinationFolder,
    destinationProject,
    destinationFolderId,
    destinationProjectId,
    context,
  ]);

  const handleFilenameChange = async (
    itemId: string,
    newBaseName: string,
    extension: string
  ) => {
    const fullName = `${newBaseName}${extension ? `.${extension}` : ''}`;
    const currentName = updatedFileNames[itemId];
    if (fullName === currentName) return;

    setLoadingByItemId((prev) => ({ ...prev, [itemId]: true }));

    const exists = await checkFileNameExists(
      destinationProject?.groupId,
      destinationFolder.id,
      fullName
    );

    if (!exists) {
      setExistingItems((prev) => prev.filter((i) => i !== itemId));
      setConflictBehaviourByItem((prev) =>
        Object.fromEntries(
          Object.entries(prev).filter(([key]) => key !== itemId)
        )
      );
      setUpdatedFileNames((prev) => ({ ...prev, [itemId]: fullName }));
    } else if (!existingItems.some((i) => i === itemId)) {
      setExistingItems((prev) => [...prev, itemId]);
    }

    setLoadingByItemId((prev) => ({ ...prev, [itemId]: false }));
  };

  const resetFilenameToOriginal = async (itemName: string, itemId: string) => {
    const [extension, ...baseNameParts] = !itemName.includes('.')
      ? [null, itemName]
      : itemName.split('.').reverse();

    const originalBaseName = baseNameParts.reverse().join('.');
    const fullName = `${originalBaseName}${extension ? `.${extension}` : ''}`;

    setInputValues((prev) => ({ ...prev, [itemId]: originalBaseName }));

    setUpdatedFileNames((prev) => {
      const updated = { ...prev };
      delete updated[itemId];
      return updated;
    });

    setConflictBehaviourByItem((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([key]) => key !== itemId))
    );

    setLoadingByItemId((prev) => ({ ...prev, [itemId]: true }));

    const exists = await checkFileNameExists(
      destinationProject?.groupId,
      destinationFolder.id,
      fullName
    );

    if (!exists) {
      setExistingItems((prev) => prev.filter((i) => i !== itemId));
    } else if (!existingItems.some((i) => i === itemId)) {
      setExistingItems((prev) => [...prev, itemId]);
    }

    setLoadingByItemId((prev) => ({ ...prev, [itemId]: false }));
  };

  const openTargetFolderSubDrawer = () => {
    setSelectTargetFolderSubDrawerVisible(true);
  };

  const handleTargetFolderModalOk = (
    destinationFolder: DriveItem,
    destinationProjectId: ProjectId
  ) => {
    setSelectTargetFolderSubDrawerVisible(false);
    setDestinationProjectId(destinationProjectId);
    setDestinationFolderId(destinationFolder?.id);
  };
  const handleTargetFolderModalCancel = () => {
    setSelectTargetFolderSubDrawerVisible(false);
  };

  const applyToAll = (behavior: FileNameConflictBehaviour) => {
    const allBehaviors = Object.fromEntries(
      existingItems.map((item) => [item, behavior])
    );
    setConflictBehaviourByItem(allBehaviors);
  };

  const getIconName = (driveItem: DriveItem) => {
    if (checkIfOneNote(driveItem)) return 'notebook';
    if (isDriveItemFolder(driveItem)) {
      return 'folder';
    }
    return iconForMimeType(driveItem.file.mimeType);
  };

  const getIconColor = (driveItem: DriveItem) => {
    if (checkIfOneNote(driveItem)) return theme.colors.mimeType.onenote;
    if (isDriveItemFolder(driveItem)) {
      return theme.colors.base.yellow[80];
    }
    return colorForMimeType(driveItem.file.mimeType);
  };

  const resetModal = () => {
    setPendingNameCheck(null);
    setIsCheckingName(false);
    setExistingItems([]);
    setUpdatedFileNames({});
    setInputValues({});
    setConflictBehaviourByItem({});
    setLoadingByItemId({});
    setSelectedForAllBehavior(null);
    setSelectTargetFolderSubDrawerVisible(false);
    setPseudoFolders([]);
    setUploadedFiles([]);
    dispatch(
      setDocumentsMetaState({
        driveItemConflictModalState: {
          behavior: null,
          context: null,
          modelType: null,
          destinationProjectId: null,
          selectedDriveItems: [],
          destinationFolderId: null,
          sourceFolderId: null,
          sourceProjectId: null,
          selectedAttachments: [],
          sessionId: null,
          fileList: null,
        },
        showDriveItemConflictModalState: 'none',
      })
    );
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    handleFilenameChangeRef.current = handleFilenameChange;
  });

  useEffect(() => {
    if (!debouncedPendingNameCheck) return;
    const { itemId, name, extension } = debouncedPendingNameCheck;
    void handleFilenameChangeRef
      .current?.(itemId, name, extension)
      .finally(() => setIsCheckingName(false));
  }, [debouncedPendingNameCheck]);

  useEffect(() => {
    if (visible && isDestinationFolderLoaded) {
      setFileCheckProcessing(true);
      debouncedFetchExistingItems();
    }
  }, [visible, isDestinationFolderLoaded, debouncedFetchExistingItems]);

  useEffect(() => {
    setDestinationFolderId(initialDestinationFolderId);
  }, [initialDestinationFolderId]);

  useEffect(() => {
    setDestinationProjectId(initialDestinationProjectId);
  }, [initialDestinationProjectId]);

  useEffect(() => {
    if (
      destinationProjectId &&
      destinationProject?.groupId &&
      !destinationFolderId
    ) {
      setDestinationFolderId(`root-group-${destinationProject.groupId}`);
    }
  }, [destinationProjectId, destinationProject?.groupId, destinationFolderId]);

  useEffect(() => {
    const loadConfigs = async () => {
      const { data } = await apiFetchConfigurations();
      if (data) setConfigurations(data);
    };
    loadConfigs();
  }, []);

  useEffect(() => {
    async function fetchPrefix() {
      const { data } = await apiFetchDocumentPrefix(destinationProjectId);
      if (data) {
        setPrefix(data.value);
      }
    }

    if (useDocumentsPrefix && destinationProjectId) {
      fetchPrefix();
    }
  }, [useDocumentsPrefix, destinationProjectId]);

  useEffect(() => {
    if (fileList) {
      getPseudoFolders();
      getUploadedFiles();
    }
  }, [fileList, getPseudoFolders, getUploadedFiles]);

  useEffect(() => {
    if (initialMailId) setSourceMailId(initialMailId);
  }, [initialMailId]);

  useEffect(() => {
    // Wenn der Folder nicht im State ist, dann nachladen
    if (
      destinationFolderId &&
      !destinationFolder &&
      destinationProject?.groupId
    ) {
      dispatch(
        fetchDriveItemsSagaAction(
          destinationProjectId,
          destinationProject.groupId,
          destinationFolderId,
          false, // ist kein Root
          context
        )
      );
    }
  }, [
    destinationFolderId,
    destinationFolder,
    destinationProjectId,
    destinationProject?.groupId,
    dispatch,
    context,
  ]);

  useEffect(() => {
    setShowContent(false);
  }, [destinationFolderId]);

  useEffect(() => {
    if (isDestinationFolderLoaded && !fileCheckProcessing) {
      setShowContent(true);
    } else {
      setShowContent(false);
    }
  }, [isDestinationFolderLoaded, fileCheckProcessing]);

  useEffect(() => {
    if (showContent && firstInputRef.current) {
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 0);
    }
  }, [showContent]);
  //#endregion

  return (
    <Modal
      title={
        behavior === 'copy'
          ? modelType === 'messageAttachment'
            ? t('documents:duplicatedDriveItemsModal.title.save')
            : t('documents:duplicatedDriveItemsModal.title.copy', {
                count:
                  selectedDriveItems?.length ??
                  selectedAttachments?.length ??
                  0,
              })
          : behavior === 'move'
            ? t('documents:duplicatedDriveItemsModal.title.move', {
                count:
                  selectedDriveItems?.length ??
                  selectedAttachments?.length ??
                  0,
              })
            : t('documents:duplicatedDriveItemsModal.title.upload', {
                count: fileList?.length ?? 0,
              })
      }
      visible={visible}
      className={classNames(classes.root, className)}
      cancelText={t('common:cancel')}
      okText={
        behavior === 'copy'
          ? modelType === 'messageAttachment'
            ? t('documents:duplicatedDriveItemsModal.actions.save')
            : t('documents:duplicatedDriveItemsModal.actions.copy')
          : behavior === 'move'
            ? t('documents:duplicatedDriveItemsModal.actions.move')
            : t('documents:duplicatedDriveItemsModal.actions.upload')
      }
      okButtonProps={{
        loading: copyProcessing,
        disabled:
          copyProcessing ||
          isNameCheckPending ||
          (!destinationFolderId && fileList?.length > 0) ||
          existingItems.some(
            (itemId) =>
              pseudoFolders.some((folder) => folder.id === itemId) &&
              selectedDriveItems.some(
                (driveItem) =>
                  isDriveItemFolder(driveItem) && driveItem.id === itemId
              )
          ),
      }}
      onOk={handleOkClicked}
      onClose={() => {
        resetModal();
        onClose();
      }}
      width="800px"
      destroyOnClose={true}
    >
      <Flex.Column childrenGap={theme.spacing.regular}>
        {!showContent && (
          <Flex.Column justifyContent="center" alignItems="center">
            {destinationProject && <PrioSpinner />}
            {!destinationProject
              ? t('documents:duplicatedDriveItemsModal.noProjectSelected')
              : t('documents:duplicatedDriveItemsModal.checkingFiles')}
          </Flex.Column>
        )}
        {showContent && (
          <>
            {/* 1. Items, die KEIN PseudoFolder und KEIN DriveItemFolder sind */}
            {(() => {
              const fileConflicts = existingItems.filter(
                (itemId) =>
                  !pseudoFolders.some((folder) => folder.id === itemId) &&
                  !(
                    selectedDriveItems?.some(
                      (driveItem) =>
                        isDriveItemFolder(driveItem) && driveItem.id === itemId
                    ) ?? false
                  )
              );
              return (
                fileConflicts.length > 0 && (
                  <p>
                    {fileConflicts.length > 1 ? (
                      <Trans
                        i18nKey="documents:duplicatedDriveItemsModal.duplicatedNameMessagePlural"
                        components={[<br />]}
                      />
                    ) : (
                      <Trans
                        i18nKey="documents:duplicatedDriveItemsModal.duplicatedNameMessageSingular"
                        components={[<br />]}
                      />
                    )}
                  </p>
                )
              );
            })()}

            {/* 2. Items, die ein PseudoFolder ODER ein DriveItemFolder sind */}
            {(() => {
              const folderConflicts = [
                ...(selectedDriveItems?.filter(
                  (item) =>
                    isDriveItemFolder(item) &&
                    existingItems.some((id) => id === item.id)
                ) ?? []),
                ...(pseudoFolders?.filter((folder) =>
                  existingItems.some((id) => id === folder.id)
                ) ?? []),
              ];
              return (
                folderConflicts.length > 0 && (
                  <p>
                    {folderConflicts.length > 1 ? (
                      <Trans
                        i18nKey="documents:duplicatedDriveItemsModal.duplicatedFolderNameMessagePlural"
                        components={[<br />]}
                      />
                    ) : (
                      <Trans
                        i18nKey="documents:duplicatedDriveItemsModal.duplicatedFolderNameMessageSingular"
                        components={[<br />]}
                      />
                    )}
                  </p>
                )
              );
            })()}
            {modelType !== 'messageAttachment' &&
              existingItems?.filter(
                (itemId) =>
                  !pseudoFolders.some((folder) => folder.id === itemId) &&
                  !(
                    selectedDriveItems?.some(
                      (driveItem) =>
                        isDriveItemFolder(driveItem) && driveItem.id === itemId
                    ) ?? false
                  )
              ).length > 1 && (
                <>
                  <Flex.Row
                    alignItems="center"
                    childrenGap={theme.spacing.small}
                    justifyContent="space-between"
                    className={classes.selectForEachItem}
                  >
                    <Flex.Row
                      alignItems="center"
                      childrenGap={theme.spacing.small}
                    >
                      <FontAwesomeIcon icon={['fal', 'files']} />
                      {t(
                        'documents:duplicatedDriveItemsModal.selectActionForEachItem'
                      )}
                    </Flex.Row>
                    <div className={classes.conflictBehaviourSelectWrapper}>
                      <Select
                        placeholder={t(
                          'documents:duplicatedDriveItemsModal.chooseActionForAll'
                        )}
                        showSearch={false}
                        options={conflictActions.map((action) => ({
                          label: action.label,
                          value: action.value,
                          onClick: action.onClick,
                        }))}
                        value={selectedForAllBehavior}
                        onChange={(val) => {
                          const behavior = val as FileNameConflictBehaviour;
                          setSelectedForAllBehavior(behavior);
                          applyToAll(behavior);
                        }}
                        dropdownStyle={{
                          minWidth: 'fit-content !important',
                        }}
                      />
                    </div>
                  </Flex.Row>
                  <Flex.Row alignItems="center" justifyContent="center">
                    {t(
                      'documents:duplicatedDriveItemsModal.selectActionIndividually'
                    )}
                  </Flex.Row>
                </>
              )}
            <Flex.Column
              childrenGap={theme.spacing.extraSmall}
              className={classes.conflictItemsWrapper}
            >
              {[
                ...(selectedDriveItems || []).map((item) => ({
                  ...item,
                  type: 'DriveItem',
                })),
                ...(selectedAttachments || []).map((item) => ({
                  ...item,
                  type: 'Attachment',
                })),
                ...(uploadedFiles ?? []).map((item) => ({
                  ...item,
                  id: item?.fileId,
                  name: item?.name,
                  size: item?.size,
                  type: 'File',
                })),
                ...pseudoFolders,
              ]
                ?.sort((a, b) => {
                  const aExists = existingItems.some((item) => item === a.id);
                  const bExists = existingItems.some((item) => item === b.id);

                  if (aExists && !bExists) return -1;
                  if (!aExists && bExists) return 1;

                  return 0;
                })
                .map((item, index) => {
                  if (!item) return null;

                  if (
                    (item.type === 'File' || item.type === 'PseudoFolder') &&
                    item.name === undefined
                  )
                    return null;

                  const [extension, ...baseNameParts] =
                    (item.type === 'DriveItem' &&
                      isDriveItemFolder(item as DriveItem)) ||
                    !(item.name && item.name.includes('.'))
                      ? [null, item.name]
                      : item.name?.split('.').reverse();

                  const baseName = baseNameParts.reverse().join('.');
                  const inputValue =
                    (item.type === 'DriveItem' &&
                      isDriveItemFolder(item as DriveItem)) ||
                    !(item.name && item.name.includes('.'))
                      ? (inputValues[item.id] ?? item.name)
                      : (inputValues[item.id] ?? baseName);

                  return (
                    <div
                      key={item.id}
                      className={classes.conflictItem}
                      style={
                        existingItems.some((i) => i === item.id)
                          ? {
                              border: `1px solid ${theme.colors.base.red[80]}`,
                              borderRadius: theme.borderRadius.small,
                            }
                          : undefined
                      }
                    >
                      <Flex.Row
                        childrenGap={theme.spacing.small}
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Flex.Row
                          alignItems="center"
                          justifyContent="space-between"
                          childrenGap={theme.spacing.small}
                        >
                          <div
                            style={{
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {loadingByItemId[item.id] ? (
                              <PrioSpinner size="small" />
                            ) : (
                              <FontAwesomeIcon
                                icon={
                                  item.type === 'DriveItem'
                                    ? ['fas', getIconName(item as DriveItem)]
                                    : item.type === 'PseudoFolder'
                                      ? ['fas', 'folder']
                                      : ['fal', 'paperclip']
                                }
                                color={
                                  item.type === 'DriveItem'
                                    ? getIconColor(item as DriveItem)
                                    : item.type === 'PseudoFolder'
                                      ? theme.colors.base.yellow[80]
                                      : theme.colors.base.blue[80]
                                }
                              />
                            )}
                          </div>
                          <Input
                            ref={index === 0 ? firstInputRef : null}
                            placeholder={baseName}
                            value={inputValue}
                            suffix={extension ? `.${extension}` : ''}
                            className={classes.inlineInput}
                            onChangeWithoutDebounce={(newName) => {
                              setInputValues((prev) => ({
                                ...prev,
                                [item.id]: newName,
                              }));

                              const trimmedName = newName.trim();
                              if (trimmedName === '') {
                                setPendingNameCheck(null);
                                setIsCheckingName(false);
                                return;
                              }
                              if (trimmedName === item.name) {
                                setPendingNameCheck(null);
                                setIsCheckingName(false);
                                resetFilenameToOriginal(item.name, item.id);
                                return;
                              }

                              setIsCheckingName(true);
                              setPendingNameCheck({
                                itemId: item.id,
                                name: trimmedName,
                                extension: extension ? extension : '',
                              });
                            }}
                            onBlur={(e) => {
                              const newName = e.target.value.trim();
                              if (newName === '' || newName === item.name) {
                                setPendingNameCheck(null);
                                setIsCheckingName(false);
                                resetFilenameToOriginal(item.name, item.id);
                                return;
                              }

                              setPendingNameCheck(null);
                              setIsCheckingName(true);
                              handleFilenameChange(
                                item.id,
                                newName,
                                extension ? extension : ''
                              ).finally(() => setIsCheckingName(false));
                            }}
                            onPressEnter={() => {
                              if (isNameCheckPending) return;
                              handleOkClicked();
                            }}
                          />

                          {updatedFileNames[item.id] && (
                            <Tooltip
                              overlay={t(
                                'documents:duplicatedDriveItemsModal.resetToInitialName'
                              )}
                            >
                              <FontAwesomeIcon
                                icon={['fal', 'rotate-left']}
                                onClick={() =>
                                  resetFilenameToOriginal(item.name, item.id)
                                }
                                style={{ cursor: 'pointer', marginLeft: '8px' }}
                              />
                            </Tooltip>
                          )}
                        </Flex.Row>
                        <span
                          style={{
                            color: theme.colors.application.typography.muted,
                            fontSize: theme.font.fontSize.extraSmall,
                          }}
                        >
                          {formatHumanFileSize(item.size || 0)}
                        </span>
                        {existingItems.some((i) => i === item.id) &&
                          (item.type !== 'DriveItem' ||
                            !isDriveItemFolder(item as DriveItem)) &&
                          item.type !== 'PseudoFolder' && (
                            <div
                              className={classes.conflictBehaviourSelectWrapper}
                            >
                              <Select
                                showSearch={false}
                                options={[
                                  {
                                    label: t(
                                      'documents:duplicatedDriveItemsModal.actions.copySingle'
                                    ),
                                    value: FileNameConflictBehaviour.RENAME,
                                  },
                                  ...(item.type !== 'Attachment'
                                    ? [
                                        {
                                          label: t(
                                            'documents:duplicatedDriveItemsModal.actions.overwriteSingle'
                                          ),
                                          value:
                                            FileNameConflictBehaviour.REPLACE,
                                        },
                                      ]
                                    : []),
                                ]}
                                value={
                                  conflictBehaviourByItem[item.id] ||
                                  FileNameConflictBehaviour.RENAME
                                }
                                onChange={(val) =>
                                  handleBehaviorChange(
                                    item.id,
                                    val as FileNameConflictBehaviour
                                  )
                                }
                                dropdownStyle={{
                                  minWidth: 'fit-content !important',
                                }}
                              />
                            </div>
                          )}
                      </Flex.Row>
                    </div>
                  );
                })}
            </Flex.Column>
          </>
        )}
        <br />
        <Flex.Column childrenGap={theme.spacing.extraSmall}>
          <Flex.Row alignItems="center" childrenGap={theme.spacing.small}>
            <FontAwesomeIcon icon={['fal', 'folder-tree']} />
            <span>{t('mail:messageDisplay.saveDestination')}</span>
            <Button
              onClick={openTargetFolderSubDrawer}
              iconProp={['fal', 'pen']}
              type="link"
            ></Button>
          </Flex.Row>
          <Flex.Row alignItems="center" childrenGap={theme.spacing.small}>
            <DriveItemBreadcrumb
              project={destinationProject}
              driveItem={destinationFolder}
              onItemClick={(driveItemId) => {
                if (!driveItemId || driveItemId?.includes('root-group-')) {
                  setDestinationFolderId(null);
                } else {
                  setDestinationFolderId(driveItemId);
                }
              }}
              onRootChange={(projectId) => {
                setDestinationProjectId(projectId);
                setDestinationFolderId(null);
              }}
              addProjectList
              disableLinkNavigation
            />
          </Flex.Row>
          {!destinationFolderId && fileList && (
            <div>
              <span
                style={{
                  color: theme.colors.base.red[80],
                  fontSize: theme.font.fontSize.regular,
                }}
              >
                {t('documents:errorMessages.uploadToRootFolderError')}
              </span>
            </div>
          )}
        </Flex.Column>
      </Flex.Column>
      <div style={{ zIndex: 1000, position: 'absolute' }}>
        <FolderSelectionDrawer
          visible={selectTargetFolderSubDrawerVisible}
          onOk={handleTargetFolderModalOk}
          onCancel={handleTargetFolderModalCancel}
          projectId={destinationProjectId}
          selectedFolder={destinationFolder?.id}
          excludedFolders={selectedDriveItems?.map((item) => item.id) ?? []}
          mode="modal"
          allowProjectChange
        />
      </div>
    </Modal>
  );
};

export default DriveItemConflictModal;
